import React from 'react';

import { ArrowsCounterClockwise, ThumbsDown, ThumbsUp, Trash } from 'phosphor-react';
import Lottie from 'react-lottie';
import { useSelector } from 'react-redux';

import aiCircleAnimation from 'assets/lottie/ai-circle.json';
import checkmarkAnimationData from 'assets/lottie/checkmark.json';
import Button from 'components/shared/Button';
import Close from 'components/shared/Icon/icons/Close';
import Tooltip, { VARIANTS } from 'components/shared/Tooltip';
import Text from 'components/shared/Typography/Text';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useTooltipDialog } from 'hooks/useTooltipDialog/useTooltipDialog';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';

import s from './AICard.module.css';
import * as t from './AICard.types';

export const AICard = (props: t.AICardProps) => {
  const lottieOptions = (() => {
    if (props.state === 'completed') {
      return {
        loop: false,
        autoplay: true,
        animationData: checkmarkAnimationData,
      };
    }

    return {
      loop: true,
      autoplay: true,
      animationData: aiCircleAnimation,
    };
  })();

  const Content = {
    idle: AICardContentIdle,
    completed: AICardContentCompleted,
    loading: AICardContentLoading,
  }[props.state];

  return (
    <div className={s.wrapper}>
      <div className={s.imageWrapper}>
        <Lottie options={lottieOptions} height={60} width={60} />
      </div>

      <Content {...props} />
    </div>
  );
};

const AICardContent = (props: t.AICardContentProps) => {
  const { title, text, children } = props;

  return (
    <div className={s.contentWrapper}>
      <div className={s.leftBlock}>
        <div className={s.textsWrapper}>
          <div className={s.titleWrapper}>
            <Text type="brandon" isStrong>
              {title}
            </Text>
          </div>
          <div>
            <Text size="medium" type="brandon">
              {text}
            </Text>
          </div>
        </div>
      </div>

      <div className={s.rightBlock}>
        <div className={s.actionsWrapper}>{children}</div>
      </div>
    </div>
  );
};

const AICardContentIdle = (props: t.AICardProps) => {
  const { onClose, onGenerate } = props;
  const title = 'Get more done - in less time - with AI';
  const text = 'Would you like to use AI to automatically draft the text on this page for you?';
  const isPageHasChanges = useSelector(appHasChangesSelector);
  const generateTooltipDialog = useTooltipDialog({
    message: 'If you choose to re-generate, the existing text will be lost. Are you sure you want to continue?',
    options: [{ text: 'No' }, { text: 'Yes', onClick: onGenerate }],
  });

  const handleGenerate = () => {
    if (isPageHasChanges) {
      generateTooltipDialog.toggleTooltip();
      return;
    }

    onGenerate();
  };

  return (
    <AICardContent title={title} text={text}>
      <Button onClick={onClose} type="grey" size="medium">
        Dismiss
      </Button>
      <div ref={generateTooltipDialog.setReference}>
        <Button onClick={handleGenerate} type="default" size="medium" isWidthFitContent>
          Try It
        </Button>
        {generateTooltipDialog.TooltipDialog()}
      </div>
    </AICardContent>
  );
};

const AICardContentLoading = (props: t.AICardProps) => {
  const { onCancel } = props;
  const text = 'Please wait a moment while Safari Portal AI drafts your content.';

  return (
    <AICardContent text={text}>
      <Button onClick={onCancel} type="default" size="medium" isWidthFitContent>
        Cancel Request
      </Button>
    </AICardContent>
  );
};

const AICardContentCompleted = (props: t.AICardProps) => {
  const { onRate, onRetry, onClear, onClose } = props;
  const title = 'AI has drafted this page for you.';
  const text =
    'Please take a moment to review it thoroughly and adjust any text as needed to ensure that all details are accurate.';
  const isPageHasChanges = useSelector(appHasChangesSelector);
  const [rate, setRate] = React.useState<'positive' | 'negative' | null>(null);
  const retryTooltipDialog = useTooltipDialog({
    message: 'If you choose to re-generate, the existing text will be lost. Are you sure you want to continue?',
    options: [{ text: 'No' }, { text: 'Yes', onClick: onRetry }],
  });
  const claerTooltipDialog = useTooltipDialog({
    message: 'Are you sure you want to clear page?',
    options: [{ text: 'No' }, { text: 'Yes', onClick: onClear }],
  });

  const handleRate = async (newRate: 'positive' | 'negative') => {
    if (rate === newRate) {
      return;
    }

    const response = await onRate(newRate);
    if (response) {
      setRate(newRate);
    }
  };

  const handleRetry = () => {
    if (isPageHasChanges) {
      retryTooltipDialog.toggleTooltip();
      return;
    }

    onRetry();
  };

  const handleClaer = () => {
    claerTooltipDialog.toggleTooltip();
  };

  return (
    <AICardContent title={title} text={text}>
      <TooltipButton
        id="page-ai-rate-positive"
        btnType={rate === 'positive' ? 'dark-grey' : 'text'}
        tooltipText="Good Response"
        onClick={() => handleRate('positive')}
      >
        <ThumbsUp size={18} />
      </TooltipButton>

      <TooltipButton
        id="page-ai-rate-negative"
        btnType={rate === 'negative' ? 'dark-grey' : 'text'}
        tooltipText="Bad Response"
        onClick={() => handleRate('negative')}
      >
        <ThumbsDown size={18} />
      </TooltipButton>

      <div className={s.divider} />

      <div ref={retryTooltipDialog.setReference}>
        <TooltipButton
          id="page-ai-retry"
          btnType="text"
          tooltipText="Try Again"
          isHideTooltip={retryTooltipDialog.isOpen}
          onClick={handleRetry}
        >
          <ArrowsCounterClockwise size={18} />
        </TooltipButton>
        {retryTooltipDialog.TooltipDialog()}
      </div>

      <div ref={claerTooltipDialog.setReference}>
        <TooltipButton id="page-ai-clear" btnType="text" tooltipText="Clear Content" onClick={handleClaer}>
          <Trash size={18} />
        </TooltipButton>
        {claerTooltipDialog.TooltipDialog()}
      </div>

      <div className={s.divider} />

      <div>
        <Button type="text" size="small" className={s.contentCompletedButton} onClick={onClose}>
          <div className={s.dismissWrapper}>
            <Close className={s.closeIcon} />

            <Text type="brandon" size="small" className={s.dismissText}>
              DISMISS
            </Text>
          </div>
        </Button>
      </div>
    </AICardContent>
  );
};

const TooltipButton = (props: t.TooltipButtonProps) => {
  const { isMiddleScreen } = useMediaBreakpoints();

  return (
    <>
      <div data-tip data-for={props.id}>
        <Button
          onClick={props.onClick}
          shape="circle"
          size="small"
          type={props.btnType}
          className={s.contentCompletedButton}
        >
          {props.children}
        </Button>
      </div>
      {!props.isHideTooltip && (
        <Tooltip id={props.id} variant={VARIANTS.DARK} place={isMiddleScreen ? 'right' : 'top'} offset={{ bottom: 4 }}>
          {props.tooltipText}
        </Tooltip>
      )}
    </>
  );
};
